import React, { useState, useEffect, useCallback } from 'react';
import { evalApi } from '../../services/api';
import { PrimaryBtn, GhostBtn } from './AdminLayout';

// 审题面。
//
// 我们拿运营真报的反馈当种子，让模型生成一批说法不同的新题去考助理。
// 整套的前提是「生成的话要像真人会说的」—— 而这一点从来没人验证过。
// 已经踩过一次：种子是工单口气，生成出来的全是「我们该说的话」，
// 不是老板会说的话，考了等于没考。
//
// 所以这一页只问一件事：**这话像不像真人说的。**
// 判法（该拦还是该放行、门槛多少）写死在题目所属的族里，这页碰不到 ——
// 那是整套评测的锚，一旦让被测方参与定答案，分数就只是自己跟自己较劲。
//
// 一次一道。列一页 64 条，人看一眼就关了。
//
// 这一页**不需要后台账号** —— 做题的人拿的是单独发给他的一个口令，
// 口令即身份（后端按它认人，见 EVAL_KEYS）。所以路由上没套 RequireAuth，
// 也绝不能把 admin_token 带出去。
export default function EvalReview() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rewrite, setRewrite] = useState('');
  const [msg, setMsg] = useState('');
  const [key, setKey] = useState(() => evalApi.key());
  const [typing, setTyping] = useState('');

  const load = useCallback(() => {
    if (!evalApi.key()) return;
    evalApi.next()
      .then((r) => { setD(r); setRewrite(''); setMsg(''); })
      // 口令不对后端一律回 404（不说「口令错误」，那等于告诉人这儿有个东西）。
      // 所以这里也只说一句中性的，并且把口令清掉让他重输。
      .catch(() => { setKey(''); sessionStorage.removeItem('evalKey'); setMsg('这个口令进不来'); });
  }, []);
  useEffect(() => { load(); }, [load, key]);

  const enter = () => {
    const k = typing.trim();
    if (!k) return;
    evalApi.setKey(k);
    setKey(k);
  };

  const answer = async (verdict) => {
    if (!d?.item || busy) return;
    setBusy(true);
    try {
      await evalApi.review({ itemId: d.item._id, verdict, rewrite: rewrite.trim() });
      load();
    } catch (e) {
      setMsg(e?.response?.data?.error || '没存上');
    } finally { setBusy(false); }
  };

  const item = d?.item;
  const isReply = item?.kind === 'reply';
  const pct = d?.total ? Math.round((d.done / d.total) * 100) : 0;

  if (!key) {
    return (
      <div style={s.page}>
        <div style={s.title}>帮我们看几句话</div>
        <div style={s.gate}>
          <input
            style={s.input}
            value={typing}
            onChange={(e) => setTyping(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && enter()}
            placeholder="口令"
            type="password"
          />
          <PrimaryBtn onClick={enter}>进去</PrimaryBtn>
        </div>
        {msg && <div style={s.msg}>{msg}</div>}
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.title}>{isReply ? '助理这句回得怎么样' : '这话像不像真人说的'}</div>
      {d?.who && <div style={s.who2}>{d.who}</div>}

      {msg && <div style={s.msg} onClick={() => setMsg('')}>{msg}</div>}

      {/* 说明写多少字都不如一个例子。第一版只写了「像不像真人说的」，
          看的人还是会问「是看他说得对不对吗」—— 所以直接摆一组对照。
          两种题问的是两件相反的事，说明也得跟着换，不然人会一直按上一题的标准判 */}
      {isReply ? (
        <div style={s.lead}>
          下面是<b>助理真说过的话</b>。只问一件事：<b>读着舒不舒服。</b>
          <div style={s.eg}>
            <div><span style={s.egBad}>别扭</span>已为您安排…另外，想顺便了解一下您住在哪个区呢？</div>
            <div><span style={s.egOk}>挺好</span>好的，周五中午 11 点给您留着。到店报姓名和手机号就行。</div>
          </div>
          客套话太多、同一句说两遍、一口气问好几件事、刚说完正事就转头打听别的 ——
          都算别扭。<b>答得对不对不用你管</b>，那部分有程序在查。
        </div>
      ) : (
        <div style={s.lead}>
          下面这些话<b>都不是真人说的，是电脑编的</b>，我们拿它们考助理。
          编得像不像真人开口的样子，只有你们判得了。
          <div style={s.eg}>
            <div><span style={s.egBad}>不像</span>我能否申请对该评价内容进行修改？</div>
            <div><span style={s.egOk}>像</span>那个差评能不能想想办法</div>
          </div>
          <b>这句话该不该答应、该不该拦，不用你管</b> —— 那部分是写死的，
          也不该让你来定。你只回答一件事：<b>真的老板 / 博主，会这么说话吗？</b>
        </div>
      )}

      {d && (
        <div style={s.bar}>
          <div style={s.barTrack}><div style={{ ...s.barFill, width: `${pct}%` }} /></div>
          <span style={s.barNum}>{d.done} / {d.total}</span>
        </div>
      )}

      {!item ? (
        <div style={s.done}>{d ? '都判完了，谢谢。' : '加载中…'}</div>
      ) : (
        <>
          {/* 回复题必须带上一句 —— 单看助理说了什么，判不了它答没答到点上 */}
          {isReply && item.context && (
            <div style={s.ctx}>{item.context}</div>
          )}
          <div style={s.who}>
            {isReply ? '助理回' : (item.role === 'merchant' ? '商家说' : '博主说')}
          </div>
          <div style={s.say}>{item.say}</div>

          {/* 三个并排。「说不清」原来单独丢在最底下 —— 那等于没有这个选项，
              人只会在「像」和「不像」里硬挑一个，逼出来的答案比空白还坏 */}
          <div style={s.btns}>
            <PrimaryBtn onClick={() => answer('like')} disabled={busy}>{isReply ? '挺好' : '像'}</PrimaryBtn>
            <GhostBtn onClick={() => answer('unlike')} disabled={busy}>{isReply ? '别扭' : '不像'}</GhostBtn>
            <GhostBtn onClick={() => answer('unsure')} disabled={busy}>说不清</GhostBtn>
          </div>

          {/* 这一栏最值钱：它是真人会怎么说的**原话**，
              比「不像」这个判断有用得多，直接能搬进题库 */}
          <div style={s.rewriteWrap}>
            <div style={s.rewriteLabel}>
              {isReply ? '换你会怎么说？（不填也行）' : '真要说的话，他会怎么说？（不填也行）'}
            </div>
            <textarea
              style={s.textarea}
              value={rewrite}
              onChange={(e) => setRewrite(e.target.value)}
              placeholder="照他平时的口气写一句"
              rows={2}
            />
          </div>

        </>
      )}

    </div>
  );
}

const s = {
  page: { maxWidth: 560, margin: '0 auto', padding: '28px 16px 60px', fontFamily: '-apple-system,"PingFang SC","Microsoft YaHei",sans-serif' },
  title: { fontSize: 22, fontWeight: 800, color: '#18181b', marginBottom: 4 },
  who2: { fontSize: 13, color: '#9ca3af', marginBottom: 4 },
  gate: { display: 'flex', gap: 8, marginTop: 20 },
  input: { flex: 1, border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none' },
  msg: { background: '#fef2f2', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 14, marginTop: 12 },
  eg: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 13, lineHeight: 2 },
  egBad: { display: 'inline-block', width: 36, color: '#9ca3af', fontSize: 12 },
  egOk: { display: 'inline-block', width: 36, color: '#16a34a', fontSize: 12 },
  lead: { fontSize: 13, color: '#374151', background: '#fafafa', borderLeft: '3px solid #FA1C51', padding: '12px 14px', borderRadius: '0 8px 8px 0', margin: '16px 0 20px', lineHeight: 1.7 },
  bar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 },
  barTrack: { flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', background: '#FA1C51' },
  barNum: { fontSize: 13, color: '#9ca3af', minWidth: 56, textAlign: 'right' },
  ctx: { fontSize: 14, color: '#6b7280', background: '#f9fafb', borderRadius: 10, padding: '12px 14px', marginBottom: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  who: { fontSize: 13, color: '#9ca3af', marginBottom: 8 },
  say: { fontSize: 17, lineHeight: 1.75, whiteSpace: 'pre-wrap', color: '#18181b', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 18px', minHeight: 76 },
  btns: { display: 'flex', gap: 12, marginTop: 22 },
  rewriteWrap: { marginTop: 24 },
  rewriteLabel: { fontSize: 13, color: '#6b7280', marginBottom: 8 },
  textarea: { width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
  skip: { marginTop: 18, textAlign: 'center' },
  done: { textAlign: 'center', color: '#6b7280', fontSize: 15, padding: '60px 0' },
  sum: { marginTop: 36, paddingTop: 20, borderTop: '1px solid #eee' },
  sumH: { fontSize: 15, fontWeight: 700, marginBottom: 12 },
  sumRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: '1px solid #f3f4f6', gap: 12 },
  dim: { color: '#9ca3af', fontSize: 14 },
  rw: { padding: '10px 0', borderBottom: '1px solid #f3f4f6' },
  rwOld: { fontSize: 13, color: '#9ca3af', textDecoration: 'line-through' },
  rwNew: { fontSize: 14, color: '#18181b', marginTop: 4 },
};
